import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Database, Loader2, LogIn, RefreshCw, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import api, { errMsg } from "@/lib/api";

const SOURCE = { override: "Tetapan manual", atlas_env: "Atlas (.env)", local: "Database tempatan" };
const total = (c = {}) => Object.values(c).reduce((a, b) => a + b, 0);

function Counts({ counts, id }) {
  const entries = Object.entries(counts || {});
  if (!entries.length) return <p className="text-xs text-muted-foreground" data-testid={id}>Tiada koleksi (kosong)</p>;
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={id}>
      {entries.map(([k, v]) => <span key={k} className="rounded-md border border-border px-2 py-0.5 font-mono text-[11px] text-muted-foreground">{k}: <b className="text-slate-200">{v}</b></span>)}
    </div>
  );
}

function ActiveStatus({ st, onReset, busy }) {
  if (!st) return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
  return (
    <div className="space-y-2 rounded-lg border border-border p-4" data-testid="db-active-status">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-sm font-medium">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" /> Primary: {st.is_atlas ? "MongoDB Atlas" : "MongoDB"}
          <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[10px] font-semibold text-primary" data-testid="db-active-source">{SOURCE[st.source]}</span>
        </p>
        {st.source === "override" && <Button size="sm" variant="ghost" onClick={onReset} disabled={busy} data-testid="db-reset-btn"><RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Kembali ke default</Button>}
      </div>
      <p className="break-all font-mono text-[11px] text-muted-foreground" data-testid="db-active-uri">{st.uri} · db: {st.db_name}</p>
      <Counts counts={st.counts} id="db-active-counts" />
    </div>
  );
}

export default function DatabaseSettingsSection() {
  const [st, setSt] = useState(null);
  const [uri, setUri] = useState("");
  const [dbName, setDbName] = useState("postpintar");
  const [mode, setMode] = useState("merge");
  const [activate, setActivate] = useState(true);
  const [test, setTest] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [confirmUse, setConfirmUse] = useState(false);

  const load = () => api.get("/database/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); } };
  const doTest = () => run("test", async () => { const r = await api.post("/database/test", { uri, db_name: dbName }); setTest(r.data); toast.success(`Bersambung ke ${r.data.host} (MongoDB ${r.data.version})`); });
  const doSync = () => run("sync", async () => {
    const r = await api.post("/database/sync", { uri, db_name: dbName, mode, activate }, { timeout: 600000 });
    setResult(r.data); setTest(null); setUri("");
    toast.success(`Sync selesai: ${total(r.data.copied)} rekod disalin${r.data.activated ? " - Atlas kini primary" : ""}`);
    load();
  });
  const doUse = () => run("use", async () => {
    const r = await api.post("/database/use", { uri, db_name: dbName }, { timeout: 120000 });
    setTest(null); setUri("");
    toast.success(`Atlas kini primary (${total(r.data.counts)} rekod sedia ada, tiada data disalin). Sila log masuk semula.`);
    setTimeout(() => window.location.reload(), 1800);
  });
  const doReset = () => run("reset", async () => { await api.post("/database/reset"); toast.success("Kembali ke database default"); load(); });

  return (
    <section className="panel fade-up p-6 lg:col-span-2" data-testid="settings-database-section">
      <div className="flex items-start gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary"><Database className="h-4 w-4" /></div>
        <div><h2 className="font-bold">Database (MongoDB Atlas)</h2><p className="mt-1 text-sm text-muted-foreground">Masukkan connection string Atlas, salin semua data ke situ dan jadikan ia database utama.</p></div>
      </div>
      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <ActiveStatus st={st} onReset={doReset} busy={!!busy} />
        <div className="space-y-4">
          <div className="space-y-2"><Label>Connection string</Label><Input value={uri} onChange={(e) => { setUri(e.target.value); setTest(null); }} placeholder="mongodb+srv://user:pass@cluster0.xxxx.mongodb.net/" className="font-mono text-xs" data-testid="db-uri-input" /></div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2"><Label>Nama database</Label><Input value={dbName} onChange={(e) => setDbName(e.target.value)} data-testid="db-name-input" /></div>
            <div className="space-y-2"><Label>Cara sync</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger data-testid="db-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="merge">Gabung (kekal data sedia ada)</SelectItem><SelectItem value="replace">Ganti (padam data sasaran dahulu)</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <label className="flex items-center justify-between rounded-lg border border-border p-3">
            <span><span className="block text-sm font-medium">Jadikan primary selepas sync</span><span className="text-xs text-muted-foreground">App terus guna database ini</span></span>
            <Switch checked={activate} onCheckedChange={setActivate} data-testid="db-activate-switch" />
          </label>
          {test && <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs" data-testid="db-test-result"><p className="mb-2 text-emerald-200">Bersambung · MongoDB {test.version} · db "{test.db_name}" {test.has_data ? "sudah ada data" : "kosong"}</p><Counts counts={test.counts} id="db-test-counts" /></div>}
          {result && <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 text-xs" data-testid="db-sync-result"><p className="mb-2 text-primary">Disalin ({result.mode === "replace" ? "ganti" : "gabung"}){result.activated ? " · kini primary" : ""}</p><Counts counts={result.copied} id="db-sync-counts" /></div>}
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={doTest} disabled={!uri || !!busy} data-testid="db-test-btn">{busy === "test" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Uji sambungan</Button>
            <Button onClick={() => setConfirm(true)} disabled={!uri || !!busy} data-testid="db-sync-btn">{busy === "sync" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}Sync sekarang</Button>
            <Button variant="outline" onClick={() => setConfirmUse(true)} disabled={!uri || !!busy} data-testid="db-use-btn">{busy === "use" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <LogIn className="mr-2 h-4 w-4" />}Guna data di Atlas (tanpa salin)</Button>
          </div>
          <p className="text-xs text-muted-foreground">Ada data sebenar di Atlas? Tekan <b>Guna data di Atlas</b>: app terus guna database itu tanpa menulis data contoh ke dalamnya.</p>
        </div>
      </div>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="border-border bg-card" data-testid="db-sync-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Sync semua data ke database ini?</AlertDialogTitle>
            <AlertDialogDescription>Semua koleksi dari database aktif ({st?.db_name}) akan disalin ke "{dbName}". {mode === "replace" ? "Data sedia ada di sasaran akan DIPADAM dahulu." : "Rekod dengan ID sama akan ditimpa, yang lain dikekalkan."} {activate && "Selepas itu app terus guna database ini."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="db-sync-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doSync} data-testid="db-sync-accept">Ya, sync</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <AlertDialog open={confirmUse} onOpenChange={setConfirmUse}>
        <AlertDialogContent className="border-border bg-card" data-testid="db-use-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Guna database "{dbName}" sebagai primary?</AlertDialogTitle>
            <AlertDialogDescription>Tiada data disalin. App terus guna data yang sedia ada di database ini (group, post, tetapan). Data tempatan sekarang tidak dipadam dan boleh diguna semula melalui "Kembali ke default". Anda mungkin perlu log masuk semula.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="db-use-cancel">Batal</AlertDialogCancel>
            <AlertDialogAction onClick={doUse} data-testid="db-use-accept">Ya, guna database ini</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
